import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../services/api';

// ---------- helpers ----------

function aplicarMascaraCNPJ(valor) {
  const s = valor.replace(/\D/g, '').slice(0, 14);
  return s
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function formatarData(valor) {
  if (!valor) return '';
  // Converte ISO para formato date input (YYYY-MM-DD)
  const d = new Date(valor);
  if (isNaN(d)) return valor;
  return d.toISOString().slice(0, 10);
}

// ---------- sub-componentes ----------

function Spinner() {
  return (
    <div className="flex justify-center items-center py-24">
      <div className="w-8 h-8 border-2 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
    </div>
  );
}

function BannerSucesso({ mensagem, onFechar }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg mb-4 animate-in">
      <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm text-green-700 flex-1">{mensagem}</p>
      {onFechar && (
        <button onClick={onFechar} className="text-green-500 hover:text-green-700 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
    </div>
  );
}

function BannerErro({ mensagem }) {
  if (!mensagem) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg mb-4">
      <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-red-700">{mensagem}</p>
    </div>
  );
}

function BarraProgresso({ percentual }) {
  const cor =
    percentual >= 80
      ? 'bg-green-500'
      : percentual >= 40
      ? 'bg-brand-600'
      : 'bg-amber-500';
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all duration-500 ${cor}`}
          style={{ width: `${Math.min(percentual, 100)}%` }}
        />
      </div>
      <span className="text-sm font-semibold text-gray-700 w-10 text-right shrink-0">
        {percentual}%
      </span>
    </div>
  );
}

// ---------- hook de toast temporário ----------

function usarToast() {
  const [toasts, setToasts] = useState({}); // { [chave]: mensagem }
  const timers = useRef({});

  const exibirToast = useCallback((chave, mensagem) => {
    setToasts(prev => ({ ...prev, [chave]: mensagem }));
    if (timers.current[chave]) clearTimeout(timers.current[chave]);
    timers.current[chave] = setTimeout(() => {
      setToasts(prev => {
        const next = { ...prev };
        delete next[chave];
        return next;
      });
    }, 3000);
  }, []);

  const fecharToast = useCallback((chave) => {
    setToasts(prev => {
      const next = { ...prev };
      delete next[chave];
      return next;
    });
    if (timers.current[chave]) clearTimeout(timers.current[chave]);
  }, []);

  return { toasts, exibirToast, fecharToast };
}

// ---------- abas ----------

const ABAS = [
  { id: 'dados', label: 'Dados do Cliente' },
  { id: 'empresas', label: 'Empresas (CNPJs)' },
  { id: 'modulos', label: 'Módulos Contratados' },
  { id: 'anexos', label: 'Anexos Extras' },
  { id: 'credenciais', label: 'Credenciais e Acesso' },
];

// ============================================================
// ABA 1 — Dados do Cliente
// ============================================================

function AbaDados({ onboarding, onAtualizar, toasts, exibirToast, fecharToast }) {
  const { id } = useParams();
  const [razaoSocial, setRazaoSocial] = useState(onboarding.razaoSocial || '');
  const [nomeFantasia, setNomeFantasia] = useState(onboarding.nomeFantasia || '');
  const [cnpj, setCnpj] = useState(
    onboarding.cnpj ? aplicarMascaraCNPJ(onboarding.cnpj) : ''
  );
  const [dataEntrada, setDataEntrada] = useState(formatarData(onboarding.dataEntrada));
  const [responsavelFinanceiro, setResponsavelFinanceiro] = useState(onboarding.responsavelFinanceiro || '');
  const [nomeAdministrador, setNomeAdministrador] = useState(onboarding.nomeAdministrador || '');
  const [emailCliente, setEmailCliente] = useState(onboarding.emailCliente || '');
  const [finalizado, setFinalizado] = useState(!!onboarding.finalizado);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  // Estados para envio de e-mail
  const [enviandoBoasVindas, setEnviandoBoasVindas] = useState(false);
  const [enviandoOnboarding, setEnviandoOnboarding] = useState(false);
  const [erroEmail, setErroEmail] = useState('');

  async function handleSalvar(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await api.put(`/admin/onboardings/${id}`, {
        razaoSocial,
        nomeFantasia,
        cnpj: cnpj.replace(/\D/g, ''),
        dataEntrada,
        responsavelFinanceiro,
        nomeAdministrador,
        emailCliente,
        finalizado,
      });
      exibirToast('dados', 'Dados do cliente salvos com sucesso.');
      onAtualizar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  async function handleEnviarBoasVindas() {
    if (!emailCliente.trim()) { setErroEmail('Informe o e-mail do cliente antes de enviar.'); return; }
    setErroEmail('');
    setEnviandoBoasVindas(true);
    try {
      await api.post(`/admin/onboardings/${id}/enviar-email`, { emailDestinatario: emailCliente.trim() });
      exibirToast('emailBoasVindas', 'E-mail de boas-vindas enviado com sucesso.');
    } catch (err) {
      setErroEmail(err.response?.data?.erro || 'Erro ao enviar e-mail de boas-vindas.');
    } finally {
      setEnviandoBoasVindas(false);
    }
  }

  async function handleEnviarOnboarding() {
    if (!emailCliente.trim()) { setErroEmail('Informe o e-mail do cliente antes de enviar.'); return; }
    setErroEmail('');
    setEnviandoOnboarding(true);
    try {
      await api.post(`/admin/onboardings/${id}/enviar-email-onboarding`, { emailDestinatario: emailCliente.trim() });
      exibirToast('emailOnboarding', 'E-mail de onboarding enviado com sucesso.');
    } catch (err) {
      setErroEmail(err.response?.data?.erro || 'Erro ao enviar e-mail de onboarding.');
    } finally {
      setEnviandoOnboarding(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-6">
      {toasts.dados && (
        <BannerSucesso mensagem={toasts.dados} onFechar={() => fecharToast('dados')} />
      )}
      <BannerErro mensagem={erro} />

      {/* Card de progresso */}
      <div className="card">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Progresso do Onboarding</p>
        <BarraProgresso percentual={onboarding.percentual ?? 0} />
        <p className="text-xs text-gray-400 mt-2">
          {onboarding.percentual >= 100
            ? 'Todos os itens foram preenchidos.'
            : `${onboarding.percentual ?? 0}% concluído — o cliente ainda possui pendências.`}
        </p>
      </div>

      {/* Campos editáveis */}
      <div className="card space-y-4">
        <h2 className="section-title">Informações da Empresa</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label">Razão Social *</label>
            <input
              type="text"
              value={razaoSocial}
              onChange={e => setRazaoSocial(e.target.value)}
              className="input"
              placeholder="Razão social completa"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Nome Fantasia *</label>
            <input
              type="text"
              value={nomeFantasia}
              onChange={e => setNomeFantasia(e.target.value)}
              className="input"
              placeholder="Nome fantasia"
              required
            />
          </div>
          <div>
            <label className="label">CNPJ *</label>
            <input
              type="text"
              value={cnpj}
              onChange={e => setCnpj(aplicarMascaraCNPJ(e.target.value))}
              className="input font-mono"
              placeholder="XX.XXX.XXX/XXXX-XX"
              maxLength={18}
              required
            />
          </div>
          <div>
            <label className="label">Data de Entrada *</label>
            <input
              type="date"
              value={dataEntrada}
              onChange={e => setDataEntrada(e.target.value)}
              className="input"
              required
            />
          </div>
          <div>
            <label className="label">Responsável Financeiro *</label>
            <input
              type="text"
              value={responsavelFinanceiro}
              onChange={e => setResponsavelFinanceiro(e.target.value)}
              className="input"
              placeholder="Nome do responsável financeiro"
              required
            />
          </div>
          <div>
            <label className="label">Nome do Administrador do Sistema *</label>
            <input
              type="text"
              value={nomeAdministrador}
              onChange={e => setNomeAdministrador(e.target.value)}
              className="input"
              placeholder="Nome do administrador"
              required
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">E-mail do Cliente</label>
            <input
              type="email"
              value={emailCliente}
              onChange={e => setEmailCliente(e.target.value)}
              className="input"
              placeholder="email@empresa.com.br"
            />
            <p className="text-xs text-gray-400 mt-1">
              Usado para envio dos e-mails de boas-vindas, onboarding e finalização.
            </p>
          </div>
        </div>
      </div>

      {/* Ações de e-mail */}
      <div className="card">
        <h2 className="section-title">Comunicações por E-mail</h2>
        <p className="section-sub">
          E-mails enviados para <strong>{emailCliente || '—'}</strong>
          {emailCliente ? ', com cópia para a equipe HeadThink.' : '. Cadastre o e-mail do cliente acima e salve para habilitar.'}
        </p>

        {toasts.emailBoasVindas && (
          <BannerSucesso mensagem={toasts.emailBoasVindas} onFechar={() => fecharToast('emailBoasVindas')} />
        )}
        {toasts.emailOnboarding && (
          <BannerSucesso mensagem={toasts.emailOnboarding} onFechar={() => fecharToast('emailOnboarding')} />
        )}
        {erroEmail && <BannerErro mensagem={erroEmail} />}

        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={handleEnviarBoasVindas}
            disabled={!emailCliente.trim() || enviandoBoasVindas}
            className="btn-secondary"
          >
            {enviandoBoasVindas ? 'Enviando...' : 'Enviar E-mail de Boas-Vindas'}
          </button>
          <button
            type="button"
            onClick={handleEnviarOnboarding}
            disabled={!emailCliente.trim() || enviandoOnboarding}
            className="btn-secondary"
          >
            {enviandoOnboarding ? 'Enviando...' : 'Enviar E-mail de Onboarding'}
          </button>
        </div>
      </div>

      {/* Toggle finalizado */}
      <div className="card">
        <h2 className="section-title">Status do Onboarding</h2>
        <label className="flex items-start gap-4 cursor-pointer group">
          <div className="mt-0.5">
            <input
              type="checkbox"
              checked={finalizado}
              onChange={e => setFinalizado(e.target.checked)}
              className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-400 cursor-pointer"
            />
          </div>
          <div>
            <p className="font-semibold text-gray-800 group-hover:text-brand-700 transition-colors">
              Marcar como finalizado
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              Quando marcado, o cliente não poderá mais editar os dados do onboarding.
              O portal exibirá uma mensagem de conclusão.
            </p>
            {finalizado && (
              <span className="inline-flex items-center gap-1.5 mt-2 badge-ok">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Finalizado
              </span>
            )}
            {!finalizado && (
              <span className="inline-flex items-center gap-1.5 mt-2 badge-pending">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
                Em andamento
              </span>
            )}
          </div>
        </label>
      </div>

      <div className="flex justify-end pt-2 pb-8">
        <button type="submit" disabled={salvando} className="btn-primary">
          {salvando ? 'Salvando...' : 'Salvar Dados do Cliente'}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// ABA 2 — Empresas (CNPJs)
// ============================================================

function AbaEmpresas({ onboarding, toasts, exibirToast, fecharToast }) {
  const { id } = useParams();
  const [empresas, setEmpresas] = useState(
    (onboarding.empresas || []).map(e => ({ ...e }))
  );
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  function adicionarEmpresa() {
    setEmpresas(prev => [...prev, { cnpj: '', nome: '', descricao: '' }]);
  }

  function removerEmpresa(idx) {
    setEmpresas(prev => prev.filter((_, i) => i !== idx));
  }

  function atualizarEmpresa(idx, campo, valor) {
    setEmpresas(prev => prev.map((e, i) => i === idx ? { ...e, [campo]: valor } : e));
  }

  async function handleSalvar(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await api.put(`/admin/onboardings/${id}`, { empresas });
      exibirToast('empresas', 'Lista de empresas salva com sucesso.');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-6">
      {toasts.empresas && (
        <BannerSucesso mensagem={toasts.empresas} onFechar={() => fecharToast('empresas')} />
      )}
      <BannerErro mensagem={erro} />

      <div className="card">
        <h2 className="section-title">Empresas / Filiais</h2>
        <p className="section-sub">
          Gerencie os CNPJs vinculados a este cliente. Os dados preenchidos pelo cliente
          para cada empresa ficam associados a ela.
        </p>

        {/* Aviso */}
        <div className="flex gap-2.5 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-xs text-amber-700">
          <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>
            Após remover uma empresa, os dados preenchidos pelo cliente para ela serão perdidos.
          </span>
        </div>

        <div className="space-y-3">
          {empresas.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">
              Nenhuma empresa filial cadastrada. Clique em "Adicionar Empresa" para incluir.
            </p>
          )}

          {empresas.map((emp, idx) => (
            <div key={idx} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Empresa {idx + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removerEmpresa(idx)}
                  className="btn-danger text-xs px-2 py-1"
                >
                  Remover
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="label">CNPJ</label>
                  <input
                    type="text"
                    value={emp.cnpj}
                    onChange={e => atualizarEmpresa(idx, 'cnpj', aplicarMascaraCNPJ(e.target.value))}
                    className="input font-mono"
                    placeholder="XX.XXX.XXX/XXXX-XX"
                    maxLength={18}
                  />
                </div>
                <div>
                  <label className="label">Nome *</label>
                  <input
                    type="text"
                    value={emp.nome}
                    onChange={e => atualizarEmpresa(idx, 'nome', e.target.value)}
                    className="input"
                    placeholder="Nome da empresa"
                    required
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="label">Descrição</label>
                  <textarea
                    value={emp.descricao}
                    onChange={e => atualizarEmpresa(idx, 'descricao', e.target.value)}
                    className="input resize-none"
                    rows={2}
                    placeholder="Observação ou descrição (opcional)"
                  />
                </div>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={adicionarEmpresa}
            className="btn-secondary w-full justify-center"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar Empresa
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-2 pb-8">
        <button type="submit" disabled={salvando} className="btn-primary">
          {salvando ? 'Salvando...' : 'Salvar Empresas'}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// ABA 3 — Módulos Contratados
// ============================================================

function AbaModulos({ onboarding, toasts, exibirToast, fecharToast }) {
  const { id } = useParams();
  const [todosModulos, setTodosModulos] = useState([]);
  const [modulosSelecionados, setModulosSelecionados] = useState(
    (onboarding.modulosContratados || []).map(m => (typeof m === 'object' ? m.id : m))
  );
  const [loadingModulos, setLoadingModulos] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    async function buscarModulos() {
      try {
        const { data } = await api.get('/modulos');
        setTodosModulos(data.filter(m => m.ativo !== false));
      } catch {
        // não bloqueia
      } finally {
        setLoadingModulos(false);
      }
    }
    buscarModulos();
  }, []);

  function toggleModulo(moduloId) {
    setModulosSelecionados(prev =>
      prev.includes(moduloId) ? prev.filter(m => m !== moduloId) : [...prev, moduloId]
    );
  }

  async function handleSalvar(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await api.put(`/admin/onboardings/${id}`, { modulosContratados: modulosSelecionados });
      exibirToast('modulos', 'Módulos contratados salvos com sucesso.');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-6">
      {toasts.modulos && (
        <BannerSucesso mensagem={toasts.modulos} onFechar={() => fecharToast('modulos')} />
      )}
      <BannerErro mensagem={erro} />

      <div className="card">
        <h2 className="section-title">Módulos Contratados</h2>
        <p className="section-sub">
          Selecione os módulos que este cliente contratou. Apenas os módulos marcados
          aparecerão no portal do cliente.
        </p>

        {loadingModulos ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : todosModulos.length === 0 ? (
          <p className="text-sm text-gray-400 mt-2">
            Nenhum módulo ativo cadastrado.{' '}
            <Link to="/admin/modulos" className="text-brand-600 hover:underline">
              Gerenciar módulos
            </Link>
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
            {todosModulos.map(m => {
              const marcado = modulosSelecionados.includes(m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border cursor-pointer transition-all text-sm ${
                    marcado
                      ? 'border-brand-500 bg-brand-50 text-brand-800 font-medium'
                      : 'border-gray-200 text-gray-700 hover:border-brand-300 hover:bg-brand-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => toggleModulo(m.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-400"
                  />
                  {m.nome}
                </label>
              );
            })}
          </div>
        )}

        {modulosSelecionados.length > 0 && (
          <p className="text-xs text-gray-400 mt-3">
            {modulosSelecionados.length} módulo{modulosSelecionados.length !== 1 ? 's' : ''} selecionado{modulosSelecionados.length !== 1 ? 's' : ''}.
          </p>
        )}
      </div>

      <div className="flex justify-end pt-2 pb-8">
        <button type="submit" disabled={salvando} className="btn-primary">
          {salvando ? 'Salvando...' : 'Salvar Módulos'}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// ABA 4 — Anexos Extras
// ============================================================

function AbaAnexos({ onboarding, toasts, exibirToast, fecharToast }) {
  const { id } = useParams();
  const [anexos, setAnexos] = useState(
    (onboarding.tiposAnexosExtras || []).map(a =>
      typeof a === 'string' ? { label: a } : { ...a }
    )
  );
  const [novoLabel, setNovoLabel] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  function adicionarAnexo() {
    const label = novoLabel.trim();
    if (!label) return;
    setAnexos(prev => [...prev, { label }]);
    setNovoLabel('');
  }

  function removerAnexo(idx) {
    setAnexos(prev => prev.filter((_, i) => i !== idx));
  }

  function atualizarAnexo(idx, valor) {
    setAnexos(prev => prev.map((a, i) => i === idx ? { label: valor } : a));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.preventDefault();
      adicionarAnexo();
    }
  }

  async function handleSalvar(e) {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await api.put(`/admin/onboardings/${id}`, { tiposAnexosExtras: anexos });
      exibirToast('anexos', 'Tipos de anexos extras salvos com sucesso.');
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form onSubmit={handleSalvar} className="space-y-6">
      {toasts.anexos && (
        <BannerSucesso mensagem={toasts.anexos} onFechar={() => fecharToast('anexos')} />
      )}
      <BannerErro mensagem={erro} />

      <div className="card">
        <h2 className="section-title">Anexos Extras</h2>
        <p className="section-sub">
          Tipos de documentos adicionais que este cliente deve enviar, além dos anexos padrão
          definidos pelos módulos.
        </p>

        <div className="space-y-2 mb-4">
          {anexos.length === 0 && (
            <p className="text-sm text-gray-400 py-4 text-center">
              Nenhum tipo de anexo extra cadastrado.
            </p>
          )}

          {anexos.map((a, idx) => (
            <div key={idx} className="flex gap-2 items-center">
              <input
                type="text"
                value={a.label}
                onChange={e => atualizarAnexo(idx, e.target.value)}
                className="input"
                placeholder="Ex: Contrato Social Atualizado"
              />
              <button
                type="button"
                onClick={() => removerAnexo(idx)}
                className="btn-danger shrink-0 px-2 py-2"
                title="Remover"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Adicionar novo */}
        <div className="flex gap-2 pt-2 border-t border-gray-100">
          <input
            type="text"
            value={novoLabel}
            onChange={e => setNovoLabel(e.target.value)}
            onKeyDown={handleKeyDown}
            className="input"
            placeholder="Nome do novo tipo de anexo (Enter para adicionar)"
          />
          <button
            type="button"
            onClick={adicionarAnexo}
            disabled={!novoLabel.trim()}
            className="btn-secondary shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-2 pb-8">
        <button type="submit" disabled={salvando} className="btn-primary">
          {salvando ? 'Salvando...' : 'Salvar Anexos Extras'}
        </button>
      </div>
    </form>
  );
}

// ============================================================
// ABA 5 — Credenciais e Acesso
// ============================================================

function AbaCredenciais({ onboarding, toasts, exibirToast, fecharToast }) {
  const { id } = useParams();

  // Reset de senha
  const [novaSenha, setNovaSenha] = useState(onboarding.login || '');
  const [senhaRedefinida, setSenhaRedefinida] = useState('');
  const [redefinindo, setRedefinindo] = useState(false);
  const [erroReset, setErroReset] = useState('');

  // Envio de e-mail
  const [emailDestinatario, setEmailDestinatario] = useState('');
  const [senhaParaEmail, setSenhaParaEmail] = useState('');
  const [enviandoEmail, setEnviandoEmail] = useState(false);
  const [erroEmail, setErroEmail] = useState('');

  function copiar(texto) {
    navigator.clipboard.writeText(texto).catch(() => {});
  }

  async function handleResetarSenha(e) {
    e.preventDefault();
    if (!novaSenha.trim()) {
      setErroReset('Informe a nova senha.');
      return;
    }
    setErroReset('');
    setRedefinindo(true);
    try {
      const { data } = await api.post(`/admin/onboardings/${id}/resetar-senha`, {
        novaSenha: novaSenha.trim(),
      });
      setSenhaRedefinida(data.novaSenha || novaSenha.trim());
      setSenhaParaEmail(data.novaSenha || novaSenha.trim());
      exibirToast('reset', 'Senha redefinida com sucesso.');
    } catch (err) {
      setErroReset(err.response?.data?.erro || 'Erro ao redefinir senha. Tente novamente.');
    } finally {
      setRedefinindo(false);
    }
  }

  async function handleEnviarEmail(e) {
    e.preventDefault();
    if (!emailDestinatario.trim()) {
      setErroEmail('Informe o e-mail do destinatário.');
      return;
    }
    setErroEmail('');
    setEnviandoEmail(true);
    try {
      await api.post(`/admin/onboardings/${id}/enviar-email`, {
        emailDestinatario: emailDestinatario.trim(),
        senha: senhaParaEmail,
      });
      exibirToast('email', 'E-mail de boas-vindas enviado com sucesso.');
    } catch (err) {
      setErroEmail(err.response?.data?.erro || 'Erro ao enviar e-mail. Tente novamente.');
    } finally {
      setEnviandoEmail(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Login atual */}
      <div className="card">
        <h2 className="section-title">Credenciais de Acesso</h2>
        <p className="section-sub">Login e senha do cliente para acessar o portal de onboarding.</p>

        <div>
          <label className="label">Login do cliente</label>
          <div className="flex gap-2">
            <input
              type="text"
              value={onboarding.login || '—'}
              readOnly
              className="input bg-gray-50 text-gray-600 font-mono cursor-default select-all"
            />
            <button
              type="button"
              onClick={() => copiar(onboarding.login || '')}
              className="btn-ghost shrink-0"
              title="Copiar login"
            >
              Copiar
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            O login não pode ser alterado após a criação do onboarding.
          </p>
        </div>
      </div>

      {/* Resetar senha */}
      <div className="card">
        <h2 className="section-title">Redefinir Senha</h2>
        <p className="section-sub">
          Defina uma nova senha para o cliente. Após redefinir, comunique a nova senha ao cliente.
        </p>

        {toasts.reset && (
          <BannerSucesso mensagem={toasts.reset} onFechar={() => fecharToast('reset')} />
        )}
        {erroReset && <BannerErro mensagem={erroReset} />}

        <form onSubmit={handleResetarSenha} className="space-y-4">
          <div>
            <label className="label">Nova senha *</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                className="input font-mono"
                placeholder="Digite a nova senha"
              />
              <button
                type="submit"
                disabled={redefinindo || !novaSenha.trim()}
                className="btn-primary shrink-0 whitespace-nowrap"
              >
                {redefinindo ? 'Redefinindo...' : 'Redefinir Senha'}
              </button>
            </div>
          </div>

          {/* Exibir senha redefinida */}
          {senhaRedefinida && (
            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-brand-700 uppercase tracking-wide mb-2">
                Nova senha definida
              </p>
              <div className="flex items-center justify-between">
                <p className="font-mono font-bold text-brand-900 text-lg">{senhaRedefinida}</p>
                <button
                  type="button"
                  onClick={() => copiar(senhaRedefinida)}
                  className="btn-ghost text-xs px-3 py-1.5"
                >
                  Copiar
                </button>
              </div>
              <p className="text-xs text-brand-500 mt-2">
                Guarde esta senha e repasse ao cliente.
              </p>
            </div>
          )}
        </form>
      </div>

      {/* Enviar e-mail */}
      <div className="card">
        <h2 className="section-title">Enviar E-mail ao Cliente</h2>
        <p className="section-sub">
          Envie um e-mail de boas-vindas com as credenciais de acesso ao portal.
        </p>

        {toasts.email && (
          <BannerSucesso mensagem={toasts.email} onFechar={() => fecharToast('email')} />
        )}
        {erroEmail && <BannerErro mensagem={erroEmail} />}

        <form onSubmit={handleEnviarEmail} className="space-y-4">
          <div>
            <label className="label">E-mail do destinatário *</label>
            <input
              type="email"
              value={emailDestinatario}
              onChange={e => setEmailDestinatario(e.target.value)}
              className="input"
              placeholder="email@empresa.com.br"
              required
            />
          </div>
          <div>
            <label className="label">Senha a incluir no e-mail</label>
            <input
              type="text"
              value={senhaParaEmail}
              onChange={e => setSenhaParaEmail(e.target.value)}
              className="input font-mono"
              placeholder="Senha atual ou a recém-redefinida"
            />
            <p className="text-xs text-gray-400 mt-1">
              Após redefinir a senha acima, ela é preenchida automaticamente aqui.
            </p>
          </div>

          <div className="pt-1">
            <button
              type="submit"
              disabled={enviandoEmail || !emailDestinatario.trim()}
              className="btn-primary"
            >
              {enviandoEmail ? 'Enviando...' : 'Enviar E-mail de Boas-Vindas'}
            </button>
          </div>
        </form>
      </div>

      <div className="pb-8" />
    </div>
  );
}

// ============================================================
// Página principal — EditarOnboarding
// ============================================================

export default function EditarOnboarding() {
  const { id } = useParams();
  const [onboarding, setOnboarding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('dados');
  const { toasts, exibirToast, fecharToast } = usarToast();

  useEffect(() => {
    carregarOnboarding();
  }, [id]);

  async function carregarOnboarding() {
    setLoading(true);
    setErro('');
    try {
      const { data } = await api.get(`/admin/onboardings/${id}`);
      setOnboarding(data);
    } catch {
      setErro('Erro ao carregar o onboarding. Verifique o acesso e tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  const nomeExibido = onboarding?.nomeFantasia || onboarding?.razaoSocial || `Onboarding #${id}`;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="flex items-center justify-between px-6 py-3 gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo_ht.png" alt="HeadThink" className="h-9 w-auto shrink-0" />
            <div className="h-6 w-px bg-gray-200 shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-400 leading-none truncate">
                {loading ? 'Carregando...' : nomeExibido}
              </p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                Editar Onboarding
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to={`/admin/onboarding/${id}/historico`}
              className="btn-ghost text-xs"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Ver Histórico
            </Link>
            <Link to="/admin" className="btn-ghost text-xs">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Dashboard
            </Link>
          </div>
        </div>

        {/* Tabs */}
        {!loading && onboarding && (
          <div className="flex overflow-x-auto border-t border-gray-100 px-6 gap-0">
            {ABAS.map(aba => (
              <button
                key={aba.id}
                onClick={() => setAbaAtiva(aba.id)}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                  abaAtiva === aba.id
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {aba.label}
              </button>
            ))}
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {loading ? (
          <Spinner />
        ) : erro ? (
          <div className="card">
            <BannerErro mensagem={erro} />
            <button onClick={carregarOnboarding} className="btn-secondary mt-2">
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            {abaAtiva === 'dados' && (
              <AbaDados
                onboarding={onboarding}
                onAtualizar={carregarOnboarding}
                toasts={toasts}
                exibirToast={exibirToast}
                fecharToast={fecharToast}
              />
            )}
            {abaAtiva === 'empresas' && (
              <AbaEmpresas
                onboarding={onboarding}
                toasts={toasts}
                exibirToast={exibirToast}
                fecharToast={fecharToast}
              />
            )}
            {abaAtiva === 'modulos' && (
              <AbaModulos
                onboarding={onboarding}
                toasts={toasts}
                exibirToast={exibirToast}
                fecharToast={fecharToast}
              />
            )}
            {abaAtiva === 'anexos' && (
              <AbaAnexos
                onboarding={onboarding}
                toasts={toasts}
                exibirToast={exibirToast}
                fecharToast={fecharToast}
              />
            )}
            {abaAtiva === 'credenciais' && (
              <AbaCredenciais
                onboarding={onboarding}
                toasts={toasts}
                exibirToast={exibirToast}
                fecharToast={fecharToast}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}
